import { ENDPOINTS, getFullUrl } from './apiConfig';

// Helper function to get company_id from localStorage
const getCompanyId = () => {
  try {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const user = JSON.parse(userData);
      console.log('Retrieved user data from localStorage:', user.company.id);
      // Change this line - return user.company.id instead of user.company.company_id
      return user?.company?.id;
    }
    return null;
  } catch (error) {
    console.error('Error getting company_id from localStorage:', error);
    return null;
  }
};

/**
 * Registers a new site by sending data to the API.
 * @param {object} siteData - The site data object { name, boundary: { type, coordinates } }.
 * @returns {Promise<object>} - A promise that resolves with the API response data.
 * @throws {Error} - Throws an error if the API call fails or returns a non-OK status.
 */
export const registerSite = async (siteData) => {
  const url = getFullUrl(ENDPOINTS.REGISTER_SITE);
  
  console.log('Sending POST request to:', url);
  
  // Get company_id from localStorage (remove await - it's not async)
  const company_id = getCompanyId();
  console.log('Retrieved company_id:', company_id);
  
  if (!company_id) {
    throw new Error('Company ID not found. Please login again.');
  }
  
  const payload = {
    ...siteData,
    company_id: company_id
  };
  
  console.log('Payload:', JSON.stringify(payload, null, 2));

  const token = localStorage.getItem('accessToken');
  
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorDetails = await response.json().catch(() => ({}));
      throw new Error(`API Error: ${response.status} - ${errorDetails.error || 'Failed to register site.'}`);
    }

    const result = await response.json();
    return result;

  } catch (error) {
    console.error('Network or Fetch Error:', error);
    throw error;
  }
};

/**
 * Fetches all registered sites/boundaries from the API.
 * @returns {Promise<Array<object>>} - A promise that resolves with an array of site objects.
 * @throws {Error} - Throws an error if the API call fails or returns a non-OK status.
 */
export const getAllSites = async () => {
  // Get company_id from localStorage
  const company_id = getCompanyId();
  
  if (!company_id) {
    throw new Error('Company ID not found. Please login again.');
  }
  
  const url = `${getFullUrl(ENDPOINTS.GET_ALL_SITES)}?company_id=${company_id}`;
  
  console.log('Sending GET request to fetch all sites:', url);

  const token = localStorage.getItem('accessToken');
  
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
    });

    if (!response.ok) {
      const errorDetails = await response.json().catch(() => ({}));
      throw new Error(`API Error: ${response.status} - ${errorDetails.error || 'Failed to fetch sites.'}`);
    }

    const result = await response.json();
    console.log('API Success Response (Sites):', result);
    
    return result.data || [];

  } catch (error) {
    console.error('Network or Fetch Error fetching sites:', error);
    throw error;
  }
};

/**
 * Deletes a site via the API.
 * @param {number|string} siteId - The ID of the site to delete.
 * @returns {Promise<object>} - A promise that resolves with the API response (often empty on success).
 * @throws {Error} - Throws an error if the API call fails or returns a non-OK status.
 */
export const deleteSite = async (siteId) => {
  const endpoint = ENDPOINTS.DELETE_SITE.replace(':siteId', siteId);
  const url = getFullUrl(endpoint);
  
  // Get company_id from localStorage
  const company_id = getCompanyId();
  
  if (!company_id) {
    throw new Error('Company ID not found. Please login again.');
  }

  const token = localStorage.getItem('accessToken');
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Include company_id in the request body
  const payload = {
    company_id: company_id
  };

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: headers,
      body: JSON.stringify(payload), // Send company_id in body
    });

    if (!response.ok) {
      const errorDetails = await response.json().catch(() => ({}));
      throw new Error(`API Error: ${response.status} - ${errorDetails.error || 'Failed to delete site.'}`);
    }

    const result = await response.json();
    return result;

  } catch (error) {
    console.error(`Network or Fetch Error deleting site ${siteId}:`, error);
    throw error;
  }
};

